/**
 * S3 — meet your agents (ONBOARDING-FLOW-SPEC.md S3). The specialist
 * catalog (GET /api/marketing-suite/specialists) filtered to what the
 * merchant's actual connections can feed (`required_connectors`), with
 * enable toggles pre-checked per the risk-posture answer, and the trust
 * law printed verbatim at the exact moment the merchant decides what to
 * turn on.
 *
 * This component only decides WHAT should change (the enable/disable
 * diff against persisted state); the caller owns applying it and
 * whatever S4 dispatch follows, since that needs the onboarding session
 * this component has no reason to know about.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';

import { listSpecialists } from '../../services/marketingSuiteApi';
import type { Specialist } from '../../types/marketingSuite';
import { isEligible, isPrechecked } from './treeWalk';

export const TRUST_LAW =
  'Anything that spends money or messages a human always waits for your approval.';

export interface RosterChange {
  key: string;
  enabled: boolean;
}

interface AgentRosterProps {
  connectedKeys: ReadonlySet<string>;
  posture: string | null;
  onFinish: (changes: RosterChange[]) => Promise<void>;
}

export function AgentRoster({ connectedKeys, posture, onFinish }: AgentRosterProps) {
  const [specialists, setSpecialists] = useState<Specialist[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [initialized, setInitialized] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    listSpecialists()
      .then((data) => setSpecialists(data.specialists))
      .catch((e) =>
        setLoadError(e instanceof Error ? e.message : 'Failed to load agents')
      );
  }, []);

  const roster = useMemo(
    () => (specialists ?? []).filter((s) => isEligible(s, connectedKeys)),
    [specialists, connectedKeys]
  );

  // Pre-check exactly once, when the roster first resolves — after that
  // the checkboxes are the merchant's own choice, never overwritten.
  useEffect(() => {
    if (initialized || !specialists) return;
    const defaults: Record<string, boolean> = {};
    for (const specialist of roster) {
      defaults[specialist.key] =
        specialist.enabled || isPrechecked(specialist, posture);
    }
    setEnabled(defaults);
    setInitialized(true);
  }, [initialized, specialists, roster, posture]);

  if (loadError) {
    return (
      <Text as="p" tone="critical">
        {loadError}
      </Text>
    );
  }
  if (!specialists) {
    return <SkeletonBodyText lines={4} />;
  }

  const asleepCount = specialists.length - roster.length;

  const finish = async () => {
    setFinishing(true);
    setFinishError(null);
    try {
      const changes: RosterChange[] = roster
        .filter((s) => !!enabled[s.key] !== s.enabled)
        .map((s) => ({ key: s.key, enabled: !!enabled[s.key] }));
      await onFinish(changes);
    } catch (e) {
      setFinishError(
        e instanceof Error ? e.message : 'Could not save your agent roster'
      );
    } finally {
      setFinishing(false);
    }
  };

  return (
    <BlockStack gap="300">
      <Text as="p" variant="bodyMd">
        {roster.length > 0
          ? `With what you've connected, ${roster.length} agent${roster.length === 1 ? '' : 's'} can start today.`
          : "Nothing is eligible to start yet — connect a source from Connectors to arm your first agent."}
      </Text>
      <Text as="p" variant="bodySm" fontWeight="semibold">
        {TRUST_LAW}
      </Text>
      {finishError && (
        <Text as="p" tone="critical">
          {finishError}
        </Text>
      )}
      <BlockStack gap="200">
        {roster.map((specialist) => (
          <Card key={specialist.key}>
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">
                  {specialist.name}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {specialist.mission}
                </Text>
                {specialist.recommended_action_types.length > 0 && (
                  <Badge tone="info">Proposes actions — needs your approval</Badge>
                )}
              </BlockStack>
              <Checkbox
                label="Enable"
                checked={!!enabled[specialist.key]}
                onChange={(checked) =>
                  setEnabled((prev) => ({ ...prev, [specialist.key]: checked }))
                }
              />
            </InlineStack>
          </Card>
        ))}
      </BlockStack>
      {asleepCount > 0 && (
        <Text as="p" tone="subdued" variant="bodySm">
          {`${asleepCount} more agent${asleepCount === 1 ? '' : 's'} will wake up once you connect the sources they need — any time, from Connectors.`}
        </Text>
      )}
      <InlineStack>
        <Button variant="primary" loading={finishing} onClick={() => void finish()}>
          Finish setup
        </Button>
      </InlineStack>
    </BlockStack>
  );
}
